import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { TiktokVideo, TiktokAgentConfig, SocialComment } from '@/types/outreach';

// ─── Videos ───────────────────────────────────────────────────────────────────

interface TiktokVideosResponse {
  count: number;
  results: TiktokVideo[];
}

export const useTiktokVideos = (search?: string, isMonitored?: boolean) => {
  return useQuery({
    queryKey: ['tiktok-videos', search, isMonitored],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (isMonitored !== undefined) params.is_monitored = String(isMonitored);
      const { data } = await api.get<TiktokVideosResponse>(API_ROUTES.tiktokVideos, { params });
      return data;
    },
  });
};

export const useTiktokVideoSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(API_ROUTES.tiktokVideoSync);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-videos'] });
      queryClient.invalidateQueries({ queryKey: ['tiktok-stats'] });
    },
  });
};

export const useTiktokVideoToggleMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ videoId, isMonitored }: { videoId: string; isMonitored: boolean }) => {
      const { data } = await api.patch(`${API_ROUTES.tiktokVideos}${videoId}/`, { isMonitored });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-videos'] });
    },
  });
};

// ─── Comments ─────────────────────────────────────────────────────────────────

interface TiktokCommentsResponse {
  count: number;
  results: SocialComment[];
}

export const useTiktokComments = (
  videoId?: string | null,
  search?: string,
  autoReplied?: boolean,
) => {
  return useQuery({
    queryKey: ['tiktok-comments', videoId, search, autoReplied],
    queryFn: async () => {
      const params: Record<string, string> = { platform: 'TIKTOK' };
      if (videoId) params.post_id = videoId;
      if (search) params.search = search;
      if (autoReplied !== undefined) params.auto_replied = String(autoReplied);
      const { data } = await api.get<TiktokCommentsResponse>(
        `${API_ROUTES.tiktokVideos}comments/`,
        { params },
      );
      return data;
    },
    enabled: !!videoId,
    refetchInterval: 10000,
  });
};

export const useTiktokCommentReply = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, content }: { commentId: string; content: string }) => {
      const { data } = await api.post(`${API_ROUTES.tiktokVideos}comments/${commentId}/reply/`, {
        content,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-comments'] });
    },
  });
};

export const useTiktokCommentHide = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, hidden }: { commentId: string; hidden: boolean }) => {
      const { data } = await api.post(`${API_ROUTES.tiktokVideos}comments/${commentId}/hide/`, {
        hidden,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-comments'] });
    },
  });
};

// ─── Agent Config ──────────────────────────────────────────────────────────────

export const useTiktokAgentConfigs = () => {
  return useQuery({
    queryKey: ['tiktok-agent-configs'],
    queryFn: async () => {
      const { data } = await api.get<TiktokAgentConfig[]>(API_ROUTES.tiktokAgentConfig);
      return data;
    },
  });
};

export const useTiktokSaveAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      config: Partial<TiktokAgentConfig> & { name: string; systemPrompt: string },
    ) => {
      if (config.id) {
        const { data } = await api.put<TiktokAgentConfig>(
          `${API_ROUTES.tiktokAgentConfig}${config.id}/`,
          config,
        );
        return data;
      }
      const { data } = await api.post<TiktokAgentConfig>(API_ROUTES.tiktokAgentConfig, config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-agent-configs'] });
    },
  });
};

export const useTiktokDeleteAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.tiktokAgentConfig}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-agent-configs'] });
    },
  });
};

// ─── Stats ─────────────────────────────────────────────────────────────────────

export interface TiktokStats {
  totalVideos: number;
  monitoredVideos: number;
  totalComments: number;
  autoReplied: number;
  replyRate: number;
  hiddenCount: number;
  averageSentiment: number | null;
}

export const useTiktokStats = () => {
  return useQuery({
    queryKey: ['tiktok-stats'],
    queryFn: async () => {
      const { data } = await api.get<TiktokStats>(API_ROUTES.tiktokStats);
      return data;
    },
    refetchInterval: 30000,
  });
};
